/**
 * Error patterns that are benign noise and should not be reported to Sentry.
 *
 * Passed to `Sentry.init({ ignoreErrors })`, which drops an event when the
 * message (or exception value) contains one of these strings / matches one of
 * these regexes.
 *
 * The Twilio Voice SDK fails to decode the configured incoming ringtone on some
 * clients (corporate proxy serving an HTML error page, unsupported codec,
 * partial download). It surfaces as an unhandled promise rejection but does not
 * affect call handling — the only effect is that the custom ring may not play.
 * Nothing in our own code calls decodeAudioData, so these can only be the
 * ringtone.
 *
 * It lands in Sentry as three separate issues, and the browsers word it
 * differently enough that one pattern doesn't cover them:
 *
 * - "Unable to decode audio data" — CALLINGLY-PHONE-94, and the tail of
 *   CALLINGLY-PHONE-9B ("Failed to execute 'decodeAudioData' on
 *   'BaseAudioContext': Unable to decode audio data").
 * - "Failed to execute 'decodeAudioData'" — CALLINGLY-PHONE-9C, where the fetch
 *   handed back something that wasn't audio at all, so the message ends
 *   "parameter 1 is not of type 'ArrayBuffer'" and the first pattern misses it.
 */
export const ignoredErrors: (string | RegExp)[] = [
  'Unable to decode audio data',
  "Failed to execute 'decodeAudioData'",
  // getUserMedia failures raised by the OS/hardware, not our code: the mic was
  // unplugged, held by another app, or the picked device vanished. They arrive
  // as bare DOMExceptions (no Twilio code to branch on) and surface one Sentry
  // issue per rep machine. The rep already sees the app fail to take audio.
  'Could not start audio source', // NotReadableError
  'Requested device not found', // NotFoundError
  // The rep's own connection dropping mid-request. axios reports connectivity
  // loss as exactly "Network Error"; fetch() as "Failed to fetch". Neither is
  // actionable in the client — the request retries or the rep reconnects.
  'Network Error',
  'Failed to fetch',
]

/**
 * Twilio Voice error codes that are expected operating conditions rather than
 * bugs — transient signaling/rate-limit/media states the SDK raises in the
 * field and recovers from on its own. Reported one-per-event they swamp the
 * project, so the error handlers leave a breadcrumb (which still rides along on
 * any real error that follows) instead of capturing them as their own issues.
 *
 * Only consulted by useCall's *device* error handler, and only when no call is
 * live — the same call-ongoing rule the 31000/31005/31009 branch uses. The
 * call-scoped handler captures everything: it resets the call, and the SDK's
 * fatal media path calls _disconnect() before emitting, so status() there can't
 * tell a doomed call from a live one.
 *
 * Deliberately excludes the mid-call 31000/31005/31009 signaling errors, which
 * mean a call actually dropped and are always worth reporting when one is up.
 *
 *  - 31206 RateExceeded: too many signaling requests; the SDK backs off.
 *  - 31107 ReconnectParameterInvalid: a reconnect race, typically on hangup.
 *  - 53405 Media connection failed: ICE/media path couldn't be established.
 */
export const benignTwilioErrorCodes = new Set<number>([31206, 31107, 53405])

/**
 * `createSentryPiniaPlugin` attaches every store's state to each Sentry event
 * (as the `state` context and a `pinia_state_all_stores_*.json` attachment).
 * Two stores hold credentials that must never leave the browser:
 *
 *  - `device.device` is the live Twilio Voice `Device`, whose `_token` is the
 *    rep's Twilio access token — enough to register as that client identity and
 *    place/receive calls until it expires. Only its registration state is kept.
 *  - `user.user.csrf_token` is the session's CSRF token.
 *
 * Returns a new object; the live store state is never mutated.
 */
export const piniaStateTransformer = (state: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...state }

  const deviceState = state.device as { device?: { state?: unknown } | null } | undefined
  if (deviceState) {
    out.device = { device: deviceState.device ? { state: deviceState.device.state } : null }
  }

  const userState = state.user as { user?: Record<string, unknown> | null } | undefined
  if (userState?.user && 'csrf_token' in userState.user) {
    out.user = { ...userState, user: { ...userState.user, csrf_token: '[Filtered]' } }
  }

  return out
}
